import React, { useState } from "react";
import { supabase, enableDemoMode, isDemoModeAvailable } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

type Mode = "login" | "signup";

interface LoginPageProps {
  initialMode?: Mode;
}

export function LoginPage({ initialMode = "login" }: LoginPageProps): JSX.Element {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [signupDone, setSignupDone] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      if (mode === "login") {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        // Redirect explícito: garante navegação imediata sem depender só do onAuthStateChange
        await navigate({ to: "/app" });
      } else {
        const { data, error: err } = await supabase.auth.signUp({ email, password });
        if (err) throw err;
        if (data.session) {
          // Confirmação de e-mail desligada: usuário já está logado.
          // Redirect explícito para /app.
          await navigate({ to: "/app" });
        } else {
          // Confirmação de e-mail ligada: mostrar tela "verifique seu e-mail".
          setSignupDone(true);
          setInfo("Verifique seu e-mail para confirmar a conta.");
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro inesperado. Tente novamente.";
      setError(translateAuthError(msg));
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = (): void => {
    setMode((m) => (m === "login" ? "signup" : "login"));
    setError(null);
    setInfo(null);
    setSignupDone(false);
  };

  const isLogin = mode === "login";

  const handleGoogleSignIn = async (): Promise<void> => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({ provider: "google" });
      if (err) throw err;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro inesperado. Tente novamente.";
      setError(translateAuthError(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Textura de fundo global */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 30% 50%, hsl(108 28% 8% / 0.7) 0%, transparent 70%)",
        }}
      />

      {/* ── COLUNA ESQUERDA - Editorial (55%) ── */}
      <aside
        className="hidden lg:flex lg:w-[55%] relative flex-col justify-between overflow-hidden px-14 py-10 xl:px-16"
        aria-label="Apresentação LucraLink"
      >
        {/* Gradiente lateral direita para escurecer a transição com o form */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-32"
          style={{
            background:
              "linear-gradient(to right, transparent, hsl(var(--background)))",
          }}
        />

        {/* Grao/textura sutil */}
        {/* Logo */}
        <div className="relative z-10 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          >
            <BrandLogo />
          </Link>
          <span className="label-editorial rounded-full border border-[hsl(var(--border-strong))] px-3 py-1">
            Automação Afiliados
          </span>
        </div>

        {/* Manchete principal */}
        <div className="relative z-10 max-w-[620px] space-y-7">
          <div className="space-y-4">
            <p className="label-editorial">Automação de alto rendimento</p>

            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold tracking-tight text-foreground leading-[1.12]">
              Seus canais de ofertas{" "}
              <br />
              gerando comissões{" "}
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-amber-300 bg-clip-text text-transparent">
                no piloto automático.
              </span>
            </h1>
          </div>

          {/* Faixa hairline */}
          <div
            className="h-px w-28"
            style={{
              background:
                "linear-gradient(to right, hsl(var(--primary) / 0.5), transparent)",
            }}
          />

          <div className="grid max-w-[520px] grid-cols-3 gap-px overflow-hidden rounded-xl border border-[hsl(var(--border-strong))] bg-[hsl(var(--border-strong))]">
            {[
              ["01", "captura"],
              ["02", "troca link"],
              ["03", "reposta"],
            ].map(([step, label]) => (
              <div key={step} className="bg-[hsl(var(--surface)/0.72)] px-4 py-3">
                <p className="text-tabular text-sm text-gold">{step}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-[1fr_auto] items-end gap-8">
          <div className="rounded-xl border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface)/0.72)] p-5">
            <p className="label-editorial">Plataformas integradas</p>
            <p className="mt-2 font-mono text-xs leading-relaxed text-[hsl(var(--muted-foreground)/0.78)]">
              Amazon · Shopee · Magalu · Mercado Livre · AliExpress
            </p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              O bot lê o post do grupo, preserva o texto e substitui somente o link
              pelo afiliado configurado.
            </p>
          </div>
          <p className="pb-1 text-xs text-[hsl(var(--muted-foreground)/0.42)]">
            © 2026 LucraLink
          </p>
        </div>
      </aside>

      {/* ── COLUNA DIREITA - Formulário (45%) ── */}
      <main className="flex w-full lg:w-[45%] flex-col items-center justify-center px-6 py-12 sm:px-10">

        {/* Logo mobile (visível só abaixo de lg) */}
        <div className="mb-8 lg:hidden flex flex-col items-center">
          <Link
            to="/"
            className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
          >
            <BrandLogo />
          </Link>
          <p className="mt-2 text-xs text-muted-foreground">
            Automação de afiliados para Telegram e WhatsApp
          </p>
        </div>

        <div className="w-full max-w-[400px] space-y-6">

          {/* Card de vidro escuro Dark Tech */}
          <div
            className="rounded-2xl border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface))] p-8 space-y-6 shadow-2xl backdrop-blur-xl"
          >

            {signupDone ? (
              /* ── Tela de confirmação de e-mail pós-cadastro ── */
              <div className="space-y-5 text-center">
                <CheckCircle2
                  size={40}
                  className="mx-auto text-primary"
                />
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-foreground">
                    Verifique seu e-mail
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Enviamos um link de confirmação para{" "}
                    <span className="font-medium text-foreground">{email}</span>.
                    Acesse sua caixa de entrada e clique no link para ativar sua conta.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleMode}
                  className={cn(
                    "text-sm font-medium underline-offset-4 hover:underline",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded",
                    "text-primary",
                  )}
                >
                  Voltar para o login
                </button>
              </div>
            ) : (
              <>
                {/* Título do card */}
                <div className="space-y-1">
                  <h2 className="text-2xl font-bold tracking-tight text-foreground">
                    {isLogin ? "Entrar" : "Criar conta"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {isLogin
                      ? "Acesse seu painel de automação"
                      : "Comece gratuitamente em instantes"}
                  </p>
                </div>

                {/* Formulário */}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>

                  {/* Campo E-mail */}
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="email"
                      className="label-editorial block"
                    >
                      E-mail
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      aria-describedby={error ? "auth-error" : undefined}
                    />
                  </div>

                  {/* Campo Senha */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="password"
                        className="label-editorial"
                      >
                        Senha
                      </Label>
                      {isLogin ? (
                        <span
                          className="text-xs underline-offset-4 hover:underline cursor-pointer"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                          aria-label="Esqueci minha senha"
                        >
                          esqueci?
                        </span>
                      ) : (
                        <span
                          className="text-xs"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          mín. 8 caracteres
                        </span>
                      )}
                    </div>
                    <Input
                      id="password"
                      type="password"
                      autoComplete={isLogin ? "current-password" : "new-password"}
                      placeholder={isLogin ? "" : "Mínimo 8 caracteres"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={isLogin ? undefined : 8}
                      disabled={loading}
                    />
                  </div>

                  {/* Feedback de erro */}
                  {error && (
                    <div
                      id="auth-error"
                      role="alert"
                      className="flex items-start gap-2.5 rounded-lg px-3.5 py-3 text-sm"
                      style={{
                        border: "1px solid hsl(var(--destructive) / 0.3)",
                        background: "hsl(var(--destructive) / 0.1)",
                        color: "hsl(var(--destructive))",
                      }}
                    >
                      <AlertCircle size={15} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Feedback de sucesso/info */}
                  {info && (
                    <div
                      role="status"
                      className="flex items-start gap-2.5 rounded-lg px-3.5 py-3 text-sm"
                      style={{
                        border: "1px solid hsl(var(--primary) / 0.3)",
                        background: "hsl(var(--primary) / 0.1)",
                        color: "hsl(var(--primary))",
                      }}
                    >
                      <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                      <span>{info}</span>
                    </div>
                  )}

                  {/* CTA principal - ouro */}
                  <Button
                    type="submit"
                    variant="gold"
                    size="lg"
                    className="w-full tracking-widest uppercase text-sm"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Aguarde...
                      </>
                    ) : isLogin ? (
                      "ENTRAR →"
                    ) : (
                      "CRIAR CONTA →"
                    )}
                  </Button>
                </form>

                {/* Entrar com Google */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className={cn(
                    "w-full flex items-center justify-center gap-2.5 rounded-lg border px-4 py-2.5 text-sm font-medium",
                    "transition-colors duration-150",
                    "hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                  style={{
                    borderColor: "hsl(var(--border-strong))",
                    background: "transparent",
                    color: "hsl(var(--foreground))",
                  }}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Entrar com Google
                </button>

                {/* Divisor */}
                <div className="flex items-center gap-3">
                  <div
                    className="h-px flex-1"
                    style={{ background: "hsl(var(--border-strong))" }}
                  />
                  <span
                    className="text-xs"
                    style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}
                  >
                    ou
                  </span>
                  <div
                    className="h-px flex-1"
                    style={{ background: "hsl(var(--border-strong))" }}
                  />
                </div>

                {/* Alternância login / cadastro */}
                <div className="text-center space-y-1.5">
                  <button
                    type="button"
                    onClick={toggleMode}
                    className={cn(
                      "text-sm font-medium underline-offset-4 hover:underline",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded",
                    )}
                    style={{ color: "hsl(var(--foreground))" }}
                  >
                    {isLogin ? "Criar conta grátis" : "Já tenho conta - entrar"}
                  </button>
                  <p className="text-xs" style={{ color: "hsl(var(--muted-foreground) / 0.6)" }}>
                    {isLogin
                      ? "cadastro gratuito, sem cartão de crédito"
                      : "sem cartão de crédito"}
                  </p>
                </div>

                {/* Modo demonstracao: so em build de dev com VITE_ENABLE_DEMO_MODE=true */}
                {isDemoModeAvailable() && (
                <div className="pt-2 border-t border-[hsl(var(--border))]">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      enableDemoMode();
                      window.location.href = "/app";
                    }}
                    className="w-full text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                  >
                    Explorar painel (modo demonstração, só em desenvolvimento)
                  </Button>
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

/** Traduz mensagens de erro do Supabase Auth para português */
function translateAuthError(msg: string): string {
  const lower = msg.toLowerCase();

  if (msg.includes("Invalid login credentials")) {
    return "E-mail ou senha incorretos.";
  }
  if (msg.includes("Email not confirmed")) {
    return "Confirme seu e-mail antes de entrar.";
  }
  if (
    msg.includes("User already registered") ||
    lower.includes("already registered")
  ) {
    return "Este e-mail já está cadastrado. Faça login.";
  }
  if (msg.includes("Password should be at least")) {
    return "A senha deve ter pelo menos 8 caracteres.";
  }
  // Rate limit com contagem de segundos: "For security purposes, you can only request this after X seconds"
  if (
    msg.includes("For security purposes") ||
    lower.includes("you can only request this after")
  ) {
    const match = msg.match(/after\s+(\d+)\s+second/i);
    if (match) {
      return `Aguarde ${match[1]} segundos antes de tentar novamente.`;
    }
    return "Aguarde alguns segundos antes de tentar novamente.";
  }
  // Rate limit genérico de envio de e-mail
  if (
    lower.includes("over_email_send_rate_limit") ||
    lower.includes("email rate limit") ||
    lower.includes("rate limit exceeded")
  ) {
    return "Muitos e-mails enviados em pouco tempo. Aguarde alguns minutos e tente de novo.";
  }
  if (lower.includes("rate limit")) {
    return "Muitas tentativas. Aguarde alguns minutos.";
  }
  return msg;
}
